import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import {
  Box,
  Flex,
  Icon,
  Image,
  LinkBox,
  LinkOverlay,
  Text,
  useBreakpointValue,
} from "@chakra-ui/react";
import { BiWorld } from "react-icons/bi";
import SocialAccountPropType from "../../../_types//SocialAccount";
import PostPropType from "../../../_types/Post";
import * as SocialTypes from "../../../_constants/SocialType";
import { getUrl } from "../../../_utils/url";
import { formatPostMessage } from "../../../_utils/postMessageFormatter";
import SocialAccountAvatar from "../SocialAccountAvatar/SocialAccountAvatar";
import {
  getFixedPhotosVideosOrder,
  getMediaFileMeta,
} from "../PostEditor/helpers";

const SocialPostCardLinkedIn = ({
  account,
  post,
  time,
  ...props
}) => {
  const { t } = useTranslation(["planner"]);
  const [isSmallScraperImage, setIsSmalScraperImage] = useState(true);
  const [isMediaHorizontal, setIsMediaHorizontal] = useState(false);
  const isMobile = useBreakpointValue({ base: true, md: false });
  const type = account?.social_type;

  const initScraperMediaMeta = useCallback(async () => {
    if (post?.scraper?.image) {
      const meta = await getMediaFileMeta("image", post?.scraper?.image);
      const isLarge = meta.width >= 400 && meta.height >= 400;

      setIsSmalScraperImage(!isLarge);
    } else {
      setIsSmalScraperImage(true);
    }
  }, [post?.scraper?.image, setIsSmalScraperImage]);

  const initMediaMeta = useCallback(async () => {
    if (post?.videos?.length) {
      if (post.videos.length === 1) {
        const item = post.videos[0];
        const meta =
          item.meta?.width && item.meta?.height
            ? item.meta
            : await getMediaFileMeta("video", item.url);

        setIsMediaHorizontal(meta.width / meta.height > 1);
      } else {
        setIsMediaHorizontal(false);
      }
    } else if (post?.photos?.length) {
      if (post.photos.length === 1) {
        const item = post.photos[0];
        const meta =
          item.meta?.width && item.meta?.height
            ? item.meta
            : await getMediaFileMeta("image", item.url);

        setIsMediaHorizontal(meta.width / meta.height > 1);
      } else {
        setIsMediaHorizontal(false);
      }
    } else {
      setIsMediaHorizontal(false);
    }
  }, [post?.photos, post?.videos, setIsMediaHorizontal]);

  useEffect(() => {
    initScraperMediaMeta();
  }, [post?.scraper?.image]);

  useEffect(() => {
    initMediaMeta();
  }, [post?.photos, post?.videos]);

  if (!account || !post || !type) {
    return null;
  }

  const renderHeader = () => (
    <Box>
      <Flex sx={{ alignItems: "flex-start", padding: "12px 16px" }}>
        <Box
          sx={{
            position: "relative",
            width: "48px",
            height: "48px",
            lineHeight: "48px",
          }}
        >
          <SocialAccountAvatar
            sx={{
              width: "full",
              height: "full",
              fontSize: "16px",
              borderRadius: type === SocialTypes.LINKEDIN_PROFILE ? "full" : null,
            }}
            name={account.name}
            src={account.image}
            socialType={account.social_type}
          />
        </Box>
        <Box sx={{ flex: "1 1 0%", margin: "0 8px" }}>
          <Box
            sx={{
              fontSize: "14px",
              fontWeight: "600",
              lineHeight: "1.42857",
              color: "rgba(0, 0, 0, 0.9)",
            }}
          >
            <Box>{account.name}</Box>
          </Box>
          <Flex
            sx={{
              fontSize: "12px",
              fontWeight: "400",
              lineHeight: "1.33333",
              color: "rgba(0, 0, 0, 0.6)",
            }}
          >
            <Box>{time}</Box>
            <Box _before={{ content: '"\\2022"', margin: "0 3px" }}>
              <Icon as={BiWorld} />
            </Box>
          </Flex>
        </Box>
        {/* <Box sx={{ marginLeft: "auto" }}>
          <Box>
            {postMenu ? (
              postMenu
            ) : (
              <Box
                sx={{ fontSize: "17px", color: "rgba(0, 0, 0, 0.6)" }}
                _before={{ content: '"\\2022\\2022\\2022"' }}
              />
            )}
          </Box>
        </Box> */}
      </Flex>
    </Box>
  );

  const renderFooter = () => {
    const actionStyles = {
      flex: "1",
      alignItems: "center",
      justifyContent: "center",
      padding: "10px 8px",
    };
    const actionIconStyles = {
      fontSize: "14px",
      fontWeight: "600",
      lineHeight: "2",
      color: "rgba(0, 0, 0, 0.6)",
      boxSizing: "content-box",
      width: "24px",
      height: "24px",
      fill: "currentColor",
    };
    const actionTextStyles = {
      fontSize: "14px",
      fontWeight: "600",
      lineHeight: "2",
      color: "rgba(0, 0, 0, 0.6)",
      marginLeft: "4px",
    };

    return (
      <Box>
        <Box sx={{ padding: "4px 8px" }}>
          <Flex sx={{ alignItems: "center" }}>
            <Flex sx={actionStyles}>
              <Icon sx={actionIconStyles} viewBox="0 0 24 24">
                <path
                  // eslint-disable-next-line max-len
                  d="M19.46 11l-3.91-3.91a7 7 0 01-1.69-2.74l-.49-1.47A2.76 2.76 0 0010.76 1 2.75 2.75 0 008 3.74v1.12a9.19 9.19 0 00.46 2.85L8.89 9H4.12A2.12 2.12 0 002 11.12a2.16 2.16 0 00.92 1.76A2.11 2.11 0 002 14.62a2.14 2.14 0 001.28 2 2 2 0 00-.28 1 2.12 2.12 0 002 2.12v.14A2.12 2.12 0 007.12 22h7.49a8.08 8.08 0 003.58-.84l.31-.16H21V11zM19 19h-1l-.73.37a6.14 6.14 0 01-2.69.63H7.72a1 1 0 01-1-.72l-.25-.87-.85-.41A1 1 0 015 17l.17-1-.76-.74A1 1 0 014.27 14l.66-1.09-.73-1.1a.49.49 0 01.08-.7.48.48 0 01.34-.11h7.05l-1.31-3.92A7 7 0 0110 4.86V3.75a.77.77 0 01.75-.75.75.75 0 01.71.51L12 5a9 9 0 002.13 3.5l4.5 4.5H19z"
                  fill="currentColor"
                />
              </Icon>
              {!isMobile ? (
                <Box sx={actionTextStyles}>
                  {t("post_preview.linkedin.like")}
                </Box>
              ) : null}
            </Flex>
            <Flex sx={actionStyles}>
              <Icon sx={actionIconStyles} viewBox="0 0 24 24">
                <path
                  // eslint-disable-next-line max-len
                  d="M7 9h10v1H7zm0 4h7v-1H7zm16-2a6.78 6.78 0 01-2.84 5.61L12 22v-4H8A7 7 0 018 4h8a7 7 0 017 7zm-2 0a5 5 0 00-5-5H8a5 5 0 000 10h6v2.28L19 15a4.79 4.79 0 002-4z"
                  fill="currentColor"
                />
              </Icon>
              {!isMobile ? (
                <Box sx={actionTextStyles}>
                  {t("post_preview.linkedin.comment")}
                </Box>
              ) : null}
            </Flex>
            <Flex sx={actionStyles}>
              <Icon sx={actionIconStyles} viewBox="0 0 24 24">
                <path
                  // eslint-disable-next-line max-len
                  d="M23 12l-4.61 7H16l4-6H8a3.92 3.92 0 00-4 3.84V17a4 4 0 00.19 1.24L5.12 21H3l-.73-2.22A6.4 6.4 0 012 16.94 6 6 0 018 11h12l-4-6h2.39z"
                  fill="currentColor"
                />
              </Icon>
              {!isMobile ? (
                <Box sx={actionTextStyles}>
                  {t("post_preview.linkedin.share")}
                </Box>
              ) : null}
            </Flex>
            <Flex sx={actionStyles}>
              <Icon sx={actionIconStyles} viewBox="0 0 24 24">
                <path
                  d="M21 3L0 10l7.66 4.26L16 8l-6.26 8.34L14 24l7-21z"
                  fill="currentColor"
                />
              </Icon>
              {!isMobile ? (
                <Box sx={actionTextStyles}>
                  {t("post_preview.linkedin.send")}
                </Box>
              ) : null}
            </Flex>
          </Flex>
        </Box>
      </Box>
    );
  };

  const renderMessage = () => (
    <Box sx={{ padding: "0 16px" }}>
      <Box
        sx={{
          fontSize: "14px",
          fontWeight: "400",
          lineHeight: "1.42857",
          color: "rgba(0, 0, 0, 0.9)",
          "& a": {
            fontWeight: "600",
            color: "#0a66c2",
          },
        }}
        dangerouslySetInnerHTML={{
          __html: formatPostMessage(post.message),
        }}
      />
    </Box>
  );

  const renderMedia = () => {
    const maxItems = 5;
    const mediaItems = getFixedPhotosVideosOrder(
      post?.photos,
      post?.videos
    ).items;
    const mediaItemsSliced = mediaItems.slice(0, maxItems);
    const isSingle = mediaItems.length === 1;
    const isNaturalSize = isSingle && isMediaHorizontal;

    const mediaItemPositions = {
      1: {
        1: "0 0 0 0",
      },
      2: {
        1: "0 calc(50% + 1.01px) 0 0",
        2: "0 0 0 calc(50% + 1.01px)",
      },
      3: {
        1: "0 0 calc(33.3333% + 1.01px)",
        2: "calc(66.6667% + 1.01px) calc(50% + 1.01px) 0 0",
        3: "calc(66.6667% + 1.01px) 0 0 calc(50% + 1.01px)",
      },
      4: {
        1: "0 0 calc(33.3333% + 1.01px)",
        2: "calc(66.6667% + 1.01px) calc(66.6667% + 1.01px) 0 0",
        3: "calc(66.6667% + 1.01px) calc(33.3333% + 1.01px) 0",
        4: "calc(66.6667% + 1.01px) 0 0 calc(66.6667% + 1.01px)",
      },
      5: {
        1: "0 calc(50% + 1.01px) calc(40% + 1.01px) 0",
        2: "0 0 calc(40% + 1.01px) calc(50% + 1.01px)",
        3: "calc(60% + 1.01px) calc(66.6667% + 1.01px) 0 0",
        4: "calc(60% + 1.01px) calc(33.3333% + 1.01px) 0",
        5: "calc(60% + 1.01px) 0 0 calc(66.6667% + 1.01px)",
      },
    };

    const mediaItemStyles = {
      display: "inline-block",
      position: isNaturalSize ? "relative" : "absolute",
      inset: "0",
      width: "100%",
      height: "100%",
      verticalAlign: "top",
      objectFit: isSingle ? "contain" : "cover",
    };

    return (
      <Box
        sx={{
          marginTop: post.message ? "8px" : null,
        }}
      >
        <Box
          sx={{
            position: "relative",
            // eslint-disable-next-line no-nested-ternary
            paddingTop: isNaturalSize
              ? "0"
              : isSingle
              ? "125%"
              : mediaItemsSliced.length === 5
              ? "83.3333%"
              : "100%",
            height: isNaturalSize ? "initial" : "0",
          }}
        >
          {mediaItemsSliced.map((item, index) => (
            <Box
              key={item.data.url}
              sx={{
                position: isNaturalSize ? "relative" : "absolute",
              }}
              style={{
                inset:
                  mediaItemPositions?.[mediaItemsSliced.length]?.[index + 1] ||
                  null,
              }}
              _after={
                mediaItems.length > mediaItemsSliced.length &&
                mediaItemsSliced.length === index + 1
                  ? {
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      position: "absolute",
                      inset: "0",
                      content: `"+${
                        mediaItems.length - mediaItemsSliced.length + 1
                      }"`,
                      fontSize: "32px",
                      lineHeight: "normal",
                      fontWeight: "400",
                      color: "#ffffff",
                      background: "rgba(0, 0, 0, 0.4)",
                    }
                  : null
              }
            >
              <Box
                sx={{
                  position: "relative",
                  padding: "0",
                  width: "full",
                  height: "full",
                  overflow: "hidden",
                  background: "#eef3f8",
                }}
              >
                {item.field === "videos" ? (
                  <Box
                    as="video"
                    sx={mediaItemStyles}
                    src={item.data.url}
                    controls
                    preload="metadata"
                    poster={item.data.thumbUrl}
                  />
                ) : (
                  <Image sx={mediaItemStyles} src={item.data.url} />
                )}
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    );
  };

  const renderLink = () => {
    const url = getUrl(post?.scraper?.link || "");
    const hasScraperImage = !!post?.scraper?.image;

    return (
      <Box
        sx={{
          marginTop: post.message ? "8px" : null,
        }}
      >
        <LinkBox
          sx={{
            position: "relative",
            background: "#eef3f8",
            display: isSmallScraperImage ? "flex" : null,
            alignItems: isSmallScraperImage ? "stretch" : null,
            padding: isSmallScraperImage ? "12px 16px" : null,
          }}
        >
          {hasScraperImage ? (
            <Box
              sx={{
                width: isSmallScraperImage ? "138px" : null,
                height: isSmallScraperImage ? "72px" : null,
                marginRight: isSmallScraperImage ? "8px" : null,
              }}
            >
              <Box
                sx={{
                  position: "relative",
                  paddingTop: "52.3%",
                  height: "0",
                  overflow: "hidden",
                }}
              >
                <Image
                  sx={{
                    position: "absolute",
                    inset: "0",
                    width: "full",
                    height: "full",
                    objectFit: "cover",
                  }}
                  src={post.scraper.image}
                />
              </Box>
            </Box>
          ) : null}
          <LinkOverlay
            sx={{
              display: isSmallScraperImage ? "flex" : "block",
              alignItems: isSmallScraperImage ? "center" : null,
              width:
                hasScraperImage && isSmallScraperImage
                  ? "calc(100% - 138px - 8px)"
                  : "100%",
              padding: "8px 12px",
              borderTop: isSmallScraperImage ? null : "1px solid #e9e5df",
            }}
            href={post.scraper.link}
            isExternal
          >
            <Box sx={{ width: "full" }}>
              {post.scraper.title ? (
                <Text
                  sx={{
                    fontSize: "14px",
                    fontWeight: "600",
                    lineHeight: "1.42857",
                    color: "rgba(0, 0, 0, 0.9)",
                  }}
                  noOfLines={2}
                >
                  {post.scraper.title}
                </Text>
              ) : null}
              <Text
                sx={{
                  fontSize: "12px",
                  fontWeight: "400",
                  lineHeight: "1.33333",
                  color: "rgba(0, 0, 0, 0.9)",
                  margin: "8px 0 0",
                }}
                noOfLines={1}
              >
                {url?.hostname || ""}
              </Text>
            </Box>
          </LinkOverlay>
        </LinkBox>
      </Box>
    );
  };

  const renderComments = () => {
    const comments = (post?.comments || []).map((item, index) => ({
      ...item,
      id: index,
    }));

    if (!comments.length) {
      return null;
    }

    return (
      <Box sx={{ padding: "0 16px" }}>
        <Box sx={{ padding: "0 0 12px" }}>
          {comments.map((item) => (
            <Flex key={item.id} sx={{ margin: "12px 0 0" }}>
              <Box
                sx={{
                  position: "relative",
                  width: "40px",
                  height: "40px",
                  lineHeight: "40px",
                  borderRadius: "50%",
                  margin: "5px 4px 0 0",
                }}
              >
                <SocialAccountAvatar
                  sx={{ width: "full", height: "full", fontSize: "16px" }}
                  name={account.name}
                  src={account.image}
                  socialType={account.social_type}
                />
              </Box>
              <Box sx={{ flex: "1" }}>
                <Box
                  sx={{
                    padding: "8px 12px",
                    backgroundColor: "#f2f2f2",
                    borderRadius: "8px",
                    borderTopLeftRadius: "0",
                    color: "rgba(0, 0, 0, 0.9)",
                  }}
                >
                  <Flex sx={{ fontSize: "12px", lineHeight: "18px" }}>
                    <Box sx={{ fontWeight: "bold" }}>{account.name}</Box>
                    <Box sx={{ marginLeft: "auto" }}>
                      {`+${item.delay || 0} min`}
                    </Box>
                  </Flex>
                  <Box
                    sx={{
                      marginTop: "8px",
                      fontSize: "14px",
                      lineHeight: "20px",
                      "& a": {
                        color: "#0a66c2",
                      },
                    }}
                    dangerouslySetInnerHTML={{
                      __html: formatPostMessage(item.message),
                    }}
                  />
                </Box>
                <Flex
                  sx={{
                    padding: "4px 8px",
                    fontSize: "12px",
                    lineHeight: "16px",
                    color: "rgba(0, 0, 0, 0.6)",
                  }}
                >
                  <Box>{t("post_preview.linkedin.like")}</Box>
                  <Box
                    sx={{
                      paddingLeft: "8px",
                      marginRight: "8px",
                      borderRight: "1px solid rgba(0, 0, 0, 0.3)",
                    }}
                  />
                  <Box>{t("post_preview.linkedin.reply")}</Box>
                </Flex>
              </Box>
            </Flex>
          ))}
        </Box>
      </Box>
    );
  };

  const showMedia = !!post?.videos?.length || !!post?.photos?.length;
  const showLink = !showMedia && post?.scraper?.link;

  return (
    <Box
      sx={{
        background: "white",
        // eslint-disable-next-line max-len
        fontFamily: "-apple-system,system-ui,Helvetica,Arial,sans-serif",
        // eslint-disable-next-line max-len
        boxShadow:
          "rgba(0, 0, 0, 0.08) 0px -1px 1px 0px, rgba(0, 0, 0, 0.08) 1px 0px 1px 0px, rgba(0, 0, 0, 0.08) -1px 0px 1px 0px, rgba(0, 0, 0, 0.08) 0px 1px 1px 0px",
        borderRadius: "8px",
        maxWidth: "552px",
      }}
      {...props}
    >
      <Box>
        {renderHeader()}
        <Box>
          <Box>
            {renderMessage()}
            {showMedia ? renderMedia() : null}
            {showLink ? renderLink() : null}
          </Box>
        </Box>
        {renderFooter()}
        {renderComments()}
      </Box>
    </Box>
  );
};

SocialPostCardLinkedIn.propTypes = {
  account: SocialAccountPropType.isRequired,
  post: PostPropType.isRequired,
  time: PropTypes.string.isRequired,
};

export default SocialPostCardLinkedIn;
